import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaFlask, FaBoxOpen, FaCertificate, FaRocket } from 'react-icons/fa';

const PrivateLabelSection = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <FaFlask className="text-3xl" />,
      title: t('privateLabel.features.customRecipes.title'),
      description: t('privateLabel.features.customRecipes.description'),
      bgColor: 'bg-blue-400',
      shadowColor: 'shadow-blue-200',
      hoverShadow: 'group-hover:shadow-blue-300'
    },
    {
      icon: <FaBoxOpen className="text-3xl" />,
      title: t('privateLabel.features.packaging.title'),
      description: t('privateLabel.features.packaging.description'),
      bgColor: 'bg-purple-400',
      shadowColor: 'shadow-purple-200',
      hoverShadow: 'group-hover:shadow-purple-300'
    },
    {
      icon: <FaRocket className="text-3xl" />,
      title: t('privateLabel.features.flexibleMOQ.title'),
      description: t('privateLabel.features.flexibleMOQ.description'),
      bgColor: 'bg-orange-400',
      shadowColor: 'shadow-orange-200',
      hoverShadow: 'group-hover:shadow-orange-300'
    },
    {
      icon: <FaCertificate className="text-3xl" />,
      title: t('privateLabel.features.compliance.title'),
      description: t('privateLabel.features.compliance.description'),
      bgColor: 'bg-green-400',
      shadowColor: 'shadow-green-200',
      hoverShadow: 'group-hover:shadow-green-300'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-green-50 via-green-100/50 to-emerald-50 dark:from-dark-800 dark:via-dark-900 dark:to-dark-800 relative overflow-hidden">
      {/* Animated Background Decorative Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-10 left-10 w-72 h-72 bg-green-300/30 rounded-full blur-3xl"
      ></motion.div>
      
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
        className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-300/20 rounded-full blur-3xl"
      ></motion.div>

      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.25, 0.45, 0.25],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-lime-300/25 rounded-full blur-3xl"
      ></motion.div>

      <div className="container mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {/* Main Heading */}
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              {t('privateLabel.title.part1')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400">
                {t('privateLabel.title.part2')}
              </span>
            </h2>

            {/* Description */}
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              {t('privateLabel.description.part1')}{' '}
              <span className="font-bold text-gray-900 dark:text-white">
                {t('privateLabel.description.highlight1')}
              </span>
              . {t('privateLabel.description.part2')}{' '}
              <span className="font-bold text-gray-900 dark:text-white">
                {t('privateLabel.description.highlight2')}
              </span>.
            </p>

            {/* Features List */}
            <div className="space-y-4 mb-10">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-4 group"
                >
                  {/* Animated Icon */}
                  <motion.div
                    whileHover={{ 
                      rotate: [0, -10, 10, -10, 0],
                      scale: 1.1
                    }}
                    transition={{ duration: 0.5 }}
                    className={`flex-shrink-0 w-16 h-16 rounded-2xl ${feature.bgColor} text-white flex items-center justify-center shadow-lg ${feature.shadowColor} ${feature.hoverShadow} transition-all duration-300`}
                  >
                    {feature.icon}
                  </motion.div>
                  
                  {/* Text */}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl group"
              >
                {t('privateLabel.cta')}
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Right Column - Image/Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/images/private-label-manufacturing.webp"
                alt={t('privateLabel.imageAlt')}
                className="w-full h-[500px] object-cover"
              />
              
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              
              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="absolute bottom-6 left-6 right-6 bg-white/95 dark:bg-dark-800/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl"
              >
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                      className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1"
                    >
                      500+
                    </motion.div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {t('privateLabel.stats.customProducts')}
                    </div>
                  </div>
                  <div>
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                      className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-1"
                    >
                      50+
                    </motion.div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {t('privateLabel.stats.partnerBrands')}
                    </div>
                  </div>
                  <div>
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.6 }}
                      className="text-3xl font-bold text-lime-600 dark:text-lime-400 mb-1"
                    >
                      30+
                    </motion.div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {t('privateLabel.stats.countries')}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Animated Decorative Elements */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full blur-2xl opacity-30"
            ></motion.div>
            
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                rotate: [0, -90, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
              className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-lime-400 to-green-400 rounded-full blur-2xl opacity-30"
            ></motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default PrivateLabelSection;
